import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const ChangeUsername: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [currentUsername, setCurrentUsername] = useState('');
  const [username, setUsername] = useState('');

  useEffect(() => {
    fetchCurrentUsername();
  }, []);

  const fetchCurrentUsername = async () => {
    try {
      const response = await axios.get('/users/profile');
      setCurrentUsername(
        response.data.username || response.data.name || t('profile.defaultUser', 'User'),
      );
    } catch (error) {
      console.error('Failed to fetch username:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (username.length < 3) {
      toast.error(t('settings.username.minLength', 'Username must be at least 3 characters long'));
      return;
    }

    if (username === currentUsername) {
      toast.error(
        t(
          'settings.username.mustBeDifferent',
          'New username must be different from current username',
        ),
      );
      return;
    }

    setIsLoading(true);

    try {
      await axios.put('/users/profile', { username });
      toast.success(t('settings.username.updateSuccess', 'Username updated successfully'));
      navigate('/profile');
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t('settings.username.updateError', 'Failed to update username'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-dark-border dark:border-dark-border border-light-border">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/profile')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
            >
              <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
            </button>
            <h1 className="ml-4 text-xl font-semibold text-dark-text dark:text-dark-text text-light-text">
              {t('settings.username.title', 'Change Username')}
            </h1>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2">
                {t('settings.username.currentUsername', 'Current Username')}
              </label>
              <input
                type="text"
                value={currentUsername}
                disabled
                className="w-full px-4 py-3 bg-dark-card/50 dark:bg-dark-card/50 bg-light-surface border border-dark-border dark:border-dark-border border-light-border rounded-xl text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary"
              />
            </div>

            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-dark-text dark:text-dark-text text-light-text mb-2"
              >
                {t('settings.username.newUsername', 'New Username')}
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                minLength={3}
                maxLength={30}
                className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('settings.username.placeholder', 'Enter new username')}
              />
              <p className="mt-2 text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                {t(
                  'settings.username.requirements',
                  '3-30 characters, letters, numbers, and underscores only',
                )}
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || !username}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
            >
              {isLoading
                ? t('settings.username.updating', 'Updating...')
                : t('settings.username.updateButton', 'Update Username')}
            </button>
          </form>
        </div>
      </div>
    </MobileLayout>
  );
};

export default ChangeUsername;
